import React, { memo, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Row, Col } from "antd";
import { FieldWrapper } from "../FieldWrapper";
import { FieldComponentProps, RadioFieldConfig } from "models/formField";
import { getFieldError } from "helpers/formField.components.helper";
import styles from "./RadioField.module.scss";

/**
 * RadioField - Single-select radio component integrated with React Hook Form
 * Uses native HTML radio inputs for full styling control
 * Supports external styling via className (parentClass) and itemClassName (childClass)
 */
export const RadioField: React.FC<FieldComponentProps<RadioFieldConfig>> = memo(
  ({ config, control: controlProp, errors: errorsProp }) => {
    // Get form context if available
    const formContext = useFormContext();
    const control = controlProp ?? formContext?.control;
    const errors = errorsProp ?? formContext?.formState?.errors;

    const error = getFieldError(errors, config.name);

    // Check if current value matches an option (handles object comparison)
    const isChecked = useCallback((currentValue: any, optionValue: any) => {
      return JSON.stringify(currentValue) === JSON.stringify(optionValue);
    }, []);

    if (!control) {
      console.error(
        `RadioField "${config.name}" requires control prop or FormProvider`,
      );
      return null;
    }

    return (
      <Controller
        name={config.name}
        control={control}
        defaultValue={config.defaultValue}
        rules={config.validation}
        render={({ field }) => (
          <FieldWrapper
            label={config.label}
            name={config.name}
            error={error}
            tooltip={config.tooltip}
            required={!!config.validation?.required}
            hidden={config.hidden}
          >
            {/* Row combines external className (parentClass) with internal base styles */}
            <Row
              className={`${config.className || ""} ${styles.radio_container}`}
            >
              {config.options.map((option, index) => (
                <Col
                  key={`${config.name}_${index}`}
                  span={24}
                  className={config.itemClassName || ""}
                >
                  <input
                    type="radio"
                    id={`${config.name}_${index}`}
                    name={config.name}
                    value={JSON.stringify(option.value)}
                    checked={isChecked(field.value, option.value)}
                    disabled={config.disabled || option.disabled}
                    onChange={(e) => {
                      const parsedValue = JSON.parse(e.target.value);
                      field.onChange(parsedValue);
                      config.onChange?.(parsedValue);
                    }}
                    onClick={() => {
                      // Pass option value to onClick for navigation on re-click
                      config.onClick?.(option.value);
                    }}
                  />
                  <label htmlFor={`${config.name}_${index}`}>
                    {option.label}
                  </label>
                </Col>
              ))}
            </Row>
          </FieldWrapper>
        )}
      />
    );
  },
);

RadioField.displayName = "RadioField";
